/**
 * Notifications: inbox, preferences, and the live SSE stream.
 *
 * RECONNECT STRATEGY. `EventSource` reconnects on its own and replays `Last-Event-ID`,
 * and the server's `catchUp()` returns everything raised while the connection was down —
 * so a notification raised while a laptop was asleep still arrives. What the browser does
 * NOT handle is a connection that the server refused (a 401 after the access token aged
 * out, most commonly): `EventSource` will retry that forever at a fixed interval without
 * ever triggering the refresh interceptor, because it does not go through `fetch`.
 *
 * So: errors are counted, backoff is exponential and capped, and after a few consecutive
 * failures the stream stops and the bell falls back to polling. Polling every 60s is a
 * fine degradation; a hidden retry storm against a dead endpoint is not.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import type { Notification } from '@shared/types';

export interface InboxPage {
  items: Notification[];
  total: number;
  hasMore: boolean;
}

export function useNotificationInbox(options?: { unreadOnly?: boolean; limit?: number }): UseQueryResult<InboxPage> {
  return useQuery({
    queryKey: ['notifications', 'inbox', options?.unreadOnly === true, options?.limit ?? 25],
    queryFn: () =>
      api.get<InboxPage>('/notifications', {
        unreadOnly: options?.unreadOnly === true ? 'true' : undefined,
        limit: options?.limit ?? 25,
      }),
    staleTime: 15_000,
  });
}

export function useUnreadCount(enablePolling: boolean): UseQueryResult<{ count: number }> {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    // Only poll when the live stream is not running.
    refetchInterval: enablePolling ? 60_000 : false,
    staleTime: 15_000,
  });
}

export function useMarkRead(): ReturnType<typeof useMutation<Notification, unknown, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Notification>(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead(): ReturnType<typeof useMutation<{ marked: number }, unknown, void>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ marked: number }>('/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useArchiveNotification(): ReturnType<typeof useMutation<Notification, unknown, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Notification>(`/notifications/${id}/archive`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export type StreamStatus = 'CONNECTING' | 'LIVE' | 'RECONNECTING' | 'OFFLINE';

const MAX_CONSECUTIVE_FAILURES = 4;

/**
 * Subscribes to the notification stream while the user is signed in.
 *
 * Returns the connection status so the bell can show "live" vs "polling" — a small thing,
 * but an approver who is waiting on a request wants to know whether silence means
 * "nothing has happened" or "the connection dropped".
 */
export function useNotificationStream(enabled: boolean): StreamStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('CONNECTING');
  const sourceRef = useRef<EventSource | null>(null);
  const failuresRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const connect = useCallback(() => {
    if (typeof EventSource === 'undefined') {
      setStatus('OFFLINE');
      return;
    }
    // `withCredentials` sends the auth cookies; the endpoint is permission-checked.
    const source = new EventSource('/api/notifications/stream', { withCredentials: true });
    sourceRef.current = source;

    source.addEventListener('open', () => {
      failuresRef.current = 0;
      setStatus('LIVE');
    });

    source.addEventListener('NOTIFICATION', (event) => {
      try {
        const notification = JSON.parse((event as MessageEvent<string>).data) as Notification;
        queryClient.setQueryData<InboxPage>(['notifications', 'inbox', false, 25], (current) =>
          current === undefined
            ? current
            : { ...current, items: [notification, ...current.items].slice(0, 25), total: current.total + 1 },
        );
        void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        // An approval landing in the inbox should also refresh the approval queue.
        void queryClient.invalidateQueries({ queryKey: ['workflows', 'pending'] });
      } catch {
        // A malformed frame must not kill the stream.
      }
    });

    source.addEventListener('UNREAD_COUNT', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as { count: number };
        queryClient.setQueryData(['notifications', 'unread-count'], payload);
      } catch {
        /* ignore */
      }
    });

    source.addEventListener('error', () => {
      source.close();
      sourceRef.current = null;
      failuresRef.current += 1;
      if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        // Stop retrying. The bell polls from here on; `useUnreadCount(true)` takes over.
        setStatus('OFFLINE');
        return;
      }
      setStatus('RECONNECTING');
      const delay = Math.min(30_000, 2_000 * 2 ** (failuresRef.current - 1));
      timerRef.current = window.setTimeout(connect, delay);
    });
  }, [queryClient]);

  useEffect(() => {
    if (!enabled) {
      setStatus('OFFLINE');
      return undefined;
    }
    failuresRef.current = 0;
    setStatus('CONNECTING');
    connect();
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [enabled, connect]);

  return status;
}

// ── Preferences ──────────────────────────────────────────────────────────────

export interface NotificationEventDefinition {
  key: string;
  label: string;
  description?: string | null;
  module?: string;
  defaultChannels?: string[];
  isRequired?: boolean;
}

export interface NotificationPreference {
  eventKey: string;
  channels: string[];
  digest?: string | null;
  isEnabled?: boolean;
}

export function useNotificationEvents(): UseQueryResult<{ events: NotificationEventDefinition[] }> {
  return useQuery({
    queryKey: ['notifications', 'events'],
    queryFn: () => api.get<{ events: NotificationEventDefinition[] }>('/notifications/events'),
    staleTime: 10 * 60_000,
  });
}

export function useNotificationPreferences(): UseQueryResult<{ preferences: NotificationPreference[] }> {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => api.get<{ preferences: NotificationPreference[] }>('/notifications/preferences'),
  });
}
